import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { loginSchema, type LoginFormValues } from '../schemas/login-schema';
import { useLogin } from '../hooks/use-login';
import { cn } from '@/shared/lib/utils';

const REMEMBER_ME_KEY = 'loki:remembered-email';

function LockoutNotice({ seconds }: { seconds: number }) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining]);

  if (remaining <= 0) return null;

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const display = `${mins}:${secs.toString().padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-2 p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
      <Lock className="h-4 w-4 shrink-0" />
      <span>Account locked. Try again in {display}</span>
    </div>
  );
}

export function LoginForm() {
  const { t } = useTranslation('auth');
  const { mutate: login, isPending, error } = useLogin();
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return Boolean(localStorage.getItem(REMEMBER_ME_KEY));
    } catch {
      return false;
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: (() => {
        try {
          return localStorage.getItem(REMEMBER_ME_KEY) ?? '';
        } catch {
          return '';
        }
      })(),
    },
  });

  const onSubmit = (values: LoginFormValues) => {
    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    } catch {
      /* noop */
    }
    login(
      { email: values.email, password: values.password },
      {
        onError: (err) => {
          // Detect 429 lockout from the enriched error
          if (err && typeof err === 'object' && 'retryAfterSeconds' in err) {
            const retryAfter = (err as { retryAfterSeconds: number }).retryAfterSeconds;
            setLockoutSeconds(retryAfter);
          }
        },
      },
    );
  };

  const isLockedOut = lockoutSeconds > 0;
  const apiError = !isLockedOut && error ? getErrorMessage(error, t) : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {/* Lockout notice */}
      {isLockedOut && <LockoutNotice seconds={lockoutSeconds} />}

      {/* API-level error banner */}
      {apiError && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{apiError}</span>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email">{t('login.email')}</Label>
        <Input
          id="email"
          type="email"
          placeholder={t('login.emailPlaceholder')}
          autoComplete="email"
          autoFocus
          aria-invalid={!!errors.email}
          className={cn(errors.email && 'border-destructive')}
          {...register('email')}
        />
        {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">{t('login.password')}</Label>
        <Input
          id="password"
          type="password"
          placeholder={t('login.passwordPlaceholder')}
          autoComplete="current-password"
          aria-invalid={!!errors.password}
          className={cn(errors.password && 'border-destructive')}
          {...register('password')}
        />
        {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('login.forgotPassword')}
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="remember-me"
          checked={rememberMe}
          onCheckedChange={(c) => setRememberMe(c === true)}
        />
        <Label htmlFor="remember-me" className="font-normal text-sm cursor-pointer">
          {t('login.rememberMe')}
        </Label>
      </div>

      <Button type="submit" className="w-full" disabled={isPending || isLockedOut}>
        {isPending ? t('login.signingIn') : t('login.signIn')}
      </Button>
    </form>
  );
}

function getErrorMessage(error: unknown, t: (key: string) => string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
    if (axiosError.response?.status === 401) {
      return t('login.invalidCredentials');
    }
    if (axiosError.response?.data?.detail) {
      return axiosError.response.data.detail;
    }
  }
  return t('login.failedSignIn');
}
